import { Component, OnDestroy, OnInit } from "@angular/core";
import { AngularFireMessaging } from "@angular/fire/messaging";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { IndexedDBService } from "../../../routes/services/db/indexedDb.service";
import { FirebaseService } from "../../../routes/services/firebase.service";
import { StorageService } from "../../../routes/services/storage.service";
import { SocketService } from "../../../routes/services/socket.service";
import { AuthService } from "../../../routes/services/auth.service";
import { FarmService } from "../../../routes/services/farm.service";
import { FreeNasHeaderMenu } from "../../../routes/constant/free-nas";

@Component({
  selector: "app-free-nas-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"]
})
export class FreeNasHeaderComponent implements OnInit, OnDestroy {
  menuItems = FreeNasHeaderMenu;
  user: any = {};
  farms: Array<{ id: number; name: string }> = [];
  farm_id: number | "" = "";

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private afMessaging: AngularFireMessaging,
    private indexedDBService: IndexedDBService,
    private firebaseService: FirebaseService,
    public storageService: StorageService,
    private socketService: SocketService,
    private authService: AuthService,
    private farmService: FarmService,
    private router: Router
  ) {}

  /**
   * Carga los datos del usuario y la granja activa.
   * @returns void
   */
  ngOnInit(): void {
    this.user = JSON.parse(localStorage.getItem("USER") || "{}");
    this.farm_id = this.storageService.localValue.farm_id;
    this.getFarms();
  }

  /**
   * Cierra las suscripciones al destruir el header.
   * @returns void
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Indica si la opcion del menu corresponde a la ruta actual. Gana el enlace mas
   * largo que coincide, para que Estanques ("/free-nas") no se marque dentro de
   * ciclos y si se marque en el detalle de un estanque.
   * @param item - Opcion del menu.
   * @returns true si la opcion es la activa.
   */
  isActive(item: { link: string }): boolean {
    const url = this.router.url.split("?")[0];
    const matches = this.menuItems
      .filter((option) => url.indexOf(option.link) === 0)
      .sort((a, b) => b.link.length - a.link.length);
    return matches.length > 0 && matches[0].link === item.link;
  }

  /**
   * Obtiene las granjas del cliente para el selector del header.
   * @returns void
   */
  getFarms(): void {
    this.farmService
      .getFarms("?fields=id,name")
      .pipe(takeUntil(this.destroy$))
      .subscribe((farms) => {
        this.farms = farms || [];
      });
  }

  /**
   * Cambia la granja activa, guarda la preferencia y notifica al socket y a las vistas.
   * @param value - Id de la granja seleccionada.
   * @returns void
   */
  setFarm(value: string): void {
    const farm_id = Number(value);
    this.socketService.socketNext([{ type: "gateways.add_listener", data: { farm: farm_id } }]);
    this.indexedDBService.clearAlldb();

    this.storageService
      .editPreferences({ ...this.storageService.localValue, farm_id })
      ?.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.storageService.localValue.farm_id = farm_id;
        this.farm_id = farm_id;
        this.storageService.setItem("farmId", farm_id);
      });
  }

  /**
   * Obtiene el logo del cliente o subcliente del usuario.
   * @returns Url del logo o la imagen por defecto si no tiene.
   */
  getUserImage(): string {
    if (this.user.is_subclient && this.user.subclient && this.user.subclient.logo) {
      return this.user.subclient.logo;
    }
    if (this.user.client && this.user.client.logo) {
      return this.user.client.logo;
    }
    return "assets/img/dummy.png";
  }

  /**
   * Obtiene el nombre a mostrar del usuario.
   * @returns Nombre del cliente o el usuario.
   */
  getUserName(): string {
    if (this.user.client && this.user.client.name) {
      return this.user.client.name;
    }
    return this.user.username || "";
  }

  /**
   * Obtiene la clave i18n del tipo de usuario.
   * @returns Clave de traduccion del tipo de usuario.
   */
  getUserTypeKey(): string {
    return this.user.is_subclient ? "tables.subclient" : "tables.client";
  }

  /**
   * Cierra la sesion: elimina el token de notificaciones, invalida el token de acceso y
   * limpia los datos locales antes de volver al login.
   * @returns Promesa que se resuelve al iniciar el cierre de sesion.
   */
  async logout(): Promise<void> {
    try {
      const token: string | null = await this.afMessaging.getToken.toPromise();
      if (token) {
        await this.afMessaging.deleteToken(token).toPromise();
        await this.firebaseService.deleteByToken(token).toPromise();
      }
    } catch (error) {}

    this.authService.logout().subscribe(() => {
      this.storageService.cancelRequests();
      localStorage.removeItem("token");
      localStorage.removeItem("name_firebase");
      this.indexedDBService.clearAlldb();
      this.router.navigateByUrl("/login");
      this.storageService.animation("success", "Éxito", "Sesión cerrada");
    });
  }
}
